/**
 * src/firebase/relatorios.js
 *
 * Lógica de agregação do relatório de uma "aula" (dia de lição), extraída
 * de RelatorioDetalhado.js para poder ser reaproveitada por:
 *  1. O fechamento manual de uma aula (tela /relatorio, via router state)
 *  2. A geração RETROATIVA de relatórios de um mês passado, quando o
 *     diretor esqueceu de abrir a tela /relatorio para algum dia (botão
 *     "Gerar relatório do mês anterior" em RelatorioMensal.js) — ver
 *     `gerarRelatoriosDoMes` abaixo.
 *
 * Antes desta extração, a MESMA lógica de agrupamento por faixa etária
 * só existia dentro do useEffect de RelatorioDetalhado.js, amarrada a
 * `location.state` — ou seja, só rodava se alguém clicasse em "Fechar
 * Relatório" naquele dia específico. Se ninguém abrisse essa tela para um
 * determinado dia, nenhum documento `relatorio` era criado para ele, e
 * RelatorioMensal.js nunca mostraria aquele dia — não havia como
 * reconstruir isso depois. Esse é exatamente o requisito do README que
 * faltava: "possibilidade de gerar um relatório do mês anterior (caso
 * esteja faltando)".
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  documentId,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { buscarProfessores, filtro } from "./CRUD";

// Mesma lista de faixas etárias que já existia hardcoded em
// RelatorioDetalhado.js — mantida idêntica para não mudar a estrutura do
// relatório que RelatorioMensal.js espera.
const GRUPOS_PROGRESSAO = [
  "Berçário",
  "Maternal",
  "Primário",
  "Juniores",
  "Pré-adolescentes",
  "Adolescentes",
  "Juvenis",
  "Jovens",
  "Adultos",
  "Discipulado",
  "Classe Mista",
];

function progressaoVazia() {
  return GRUPOS_PROGRESSAO.map((nome) => ({
    nome,
    qtd: 0,
    qtdProf: 0,
    qtdAlunos: 0,
    qtdVisit: 0,
    qtdPresent: 0,
    prof: 0,
  }));
}

/**
 * Monta o documento `relatorio` de UM dia de lição (uma "aula"), a partir
 * dos documentos `aulaTurma` já fechados daquele dia e da lista completa
 * de turmas cadastradas na escola.
 *
 * @param {Array} aulaTurmasDoDia - docs de `aulaTurma` (todas as turmas
 *   daquele dia específico, TODAS com situacao "fechada"). Precisa incluir
 *   a turma "Professores".
 * @param {Array} turmasDaEscola - TODAS as turmas cadastradas na escola
 *   (não só as do dia), cada uma com {id, nome, grupo, professor: [...]}.
 *   É daqui que vem "Nº Classes"/"Nº Professores" por faixa etária no
 *   relatório mensal — reflete o quadro atual da escola, não só quem deu
 *   aula naquele dia.
 * @param {Object} aulaInfo - o doc de `aulas` (o dia em si): {licao, data, ...}
 * @return {Object} o objeto pronto para salvar em escolas/{id}/relatorio
 */
export function montarRelatorioDeAula(aulaTurmasDoDia, turmasDaEscola, aulaInfo,idEscola) {
  const turmaProfessoresOriginal = aulaTurmasDoDia.find((t) => t.nome === "Professores");
  if (!turmaProfessoresOriginal) {
    throw new Error('Turma "Professores" não encontrada entre as aulaTurma deste dia.');
  }

  // IMPORTANTE: clona os objetos antes de mexer no campo `presencas` (de
  // número pra tupla [qtdAlunosPresentes, qtdProfessoresPresentes]).
  // A versão anterior (em RelatorioDetalhado.js) mutava os objetos
  // ORIGINAIS de `location.state.listaTurmas`, que são os MESMOS objetos
  // usados pra renderizar o card-resumo de cada turma na tela — depois
  // do cálculo, `item.presencas` virava um array no meio da tela, quebrando
  // aquele card. Trabalhando em cópias, quem chamar esta função pode
  // seguir usando sua lista original pra exibir na tela sem esse efeito
  // colateral.
  const turmas = aulaTurmasDoDia.map((t) => ({ ...t }));
  const turmaProfessores = turmas.find((t) => t.nome === "Professores");
  
  turmas.forEach((t) => {
    if (t.nome !== "Professores") {
      t.presencas = [t.presencas || 0, 0];
    }
  });

  // Cruza a presença de cada PROFESSOR (registrada na turma "Professores")
  // com a turma que ele leciona. Casamento feito pelo NOME da turma
  // (`pessoa.turmaNome == turma.nome`) — fragilidade conhecida herdada do
  // código original: se duas turmas tiverem o mesmo nome, o cruzamento
  // erra. Preservado assim de propósito (não é escopo desta correção
  // mudar a forma de casar professor->turma); documentado aqui para quem
  // for mexer nisso depois.
  (turmaProfessores.listaAlunos || []).forEach((pessoa) => {
    turmas.forEach((t) => {
      if (pessoa.turma === t.idTurma && t.nome !== "Professores" && pessoa.presencas) {
        t.presencas[1] += 1;
      }
    });
  });
  const progressao = progressaoVazia();
  turmasDaEscola.forEach((turmaDef) => {
    const grupo = progressao.find((g) => g.nome === turmaDef.grupo);
    if (!grupo) return; // turma com `grupo` fora da lista conhecida — ignora, como no original

    grupo.qtd += 1;
    filtro("professores", "turma", "==", turmaDef.id,(e)=>{
      grupo.qtdProf += e.length
    }, idEscola);
   
    turmas.forEach((t) => {
      if (turmaDef.id === t.idTurma) {
        grupo.qtdAlunos += t.matriculados || 0;
        grupo.qtdVisit += t.visitantes || 0;
        grupo.qtdPresent += t.presencas[0] || 0;
        grupo.prof += t.presencas[1] || 0;
      }
    });
  });

  const totalOfertas = turmas.reduce((acc, t) => acc + (Number(t.totalOfertas) || 0), 0);

  return {
    progressao,
    data: turmas[0].data,
    ofertas: totalOfertas,
    ofertaProf: Number(turmaProfessoresOriginal.totalOfertas) || 0,
    licao: aulaInfo?.licao || null,
  };
}

/**
 * Anexa ao relatório os dados de hierarquia da escola que o gerou —
 * `regionalId`/`geralId` (se a escola estiver vinculada a alguma) e
 * identificação da escola de origem. É esse denormalização que permite
 * a Regional/Geral ler os relatórios das escolas vinculadas direto via
 * collectionGroup('relatorio').where('regionalId'|'geralId','==',meuId),
 * sem precisar "descer" a árvore. Ver backend/hierarquia.js para como
 * regionalId/geralId são setados (só via aceitarConvite, nunca client
 * direto — são campos que concedem autoridade de leitura, então não dá
 * pra confiar num valor que o client pudesse inventar).
 */
export async function anexarDadosHierarquia(relatorio, idEscola) {
  const escolaSnap = await getDoc(doc(db, "escolas", idEscola));
  const escola = escolaSnap.exists() ? escolaSnap.data() : {};
  return {
    ...relatorio,
    idEscolaOrigem: idEscola,
    nomeEscolaOrigem: escola.nome || null,
    regionalId: escola.regionalId || null,
    geralId: escola.geralId || null,
  };
}

/**
 * Busca até 30 ids em lotes de 10 (limite do operador Firestore "in"),
 * combinando os resultados. `aula.turmas` normalmente tem poucos ids
 * (uma por turma da escola), mas escolas grandes podem passar de 10.
 */
async function buscarPorIdsEmLotes(idEscola, collectionName, ids) {
  const resultados = [];
  for (let i = 0; i < ids.length; i += 10) {
    const lote = ids.slice(i, i + 10);
    const snap = await getDocs(
      query(collection(db, "escolas", idEscola, collectionName), where(documentId(), "in", lote)),
    );
    snap.docs.forEach((d) => resultados.push({ id: d.id, ...d.data() }));
  }
  return resultados;
}

/**
 * Transforma a lista "crua" de relatórios de UM mês (um doc por dia de
 * lição) nas duas estruturas que a tela usa pra exibir:
 *   - `diasOrdenados`: os mesmos docs, ordenados por dia, com uma linha
 *     "Total" adicionada ao final de cada `progressao` (soma de todas as
 *     faixas etárias daquele dia) — usado na tabela "Frequência por dia".
 *   - `crescimento`: uma linha por faixa etária, com matriculados no
 *     INÍCIO do mês (primeiro dia) e no FIM do mês (último dia) lado a
 *     lado, mais o total de visitantes do mês somado — usado na tabela
 *     "Controle de Crescimento".
 *
 * Extraído de RelatorioMensal.js (era lógica presa dentro do useEffect,
 * só rodava pro mês atual) para poder ser reaproveitado tanto pelo mês
 * atual quanto pelo histórico de meses passados (HistoricoRelatorios.js).
 *
 * IMPORTANTE: muta os objetos de `docsDoMes` (ordena o array em si e
 * empurra a linha "Total" dentro de cada `progressao`) — sempre chame com
 * um array recém-vindo do Firestore, nunca com um array que a tela ainda
 * esteja usando em outro lugar.
 *
 * @param {Array} docsDoMes - docs de `relatorio` de um único mês/ano.
 * @return {{diasOrdenados: Array, crescimento: Array}|null} null se a
 *   lista de entrada estiver vazia (nada gerado pra aquele mês ainda).
 */
export function montarCrescimentoDoMes(docsDoMes) {
  if (!docsDoMes || docsDoMes.length < 1) return null;

  const doc = [...docsDoMes];

  const ordenar = (ord) => {
    let temp = 0;
    for (let i = 0; i < ord.length - 1; i++) {
      for (let j = 0; j < ord.length - i - 1; j++) {
        if (ord[j].data.dia > ord[j + 1].data.dia) {
          temp = ord[j];
          ord[j] = ord[j + 1];
          ord[j + 1] = temp;
        }
      }
    }
  };
  ordenar(doc);

  doc.forEach((element) => {
    const novo = {
      nome: "Total",
      qtd: element.progressao.reduce((acumulador, valorTurma) => acumulador + valorTurma.qtd, 0),
      qtdAlunos: element.progressao.reduce((acumulador, valorTurma) => acumulador + valorTurma.qtdAlunos, 0),
      qtdPresent: element.progressao.reduce((acumulador, valorTurma) => acumulador + valorTurma.qtdPresent, 0),
      qtdProf: element.progressao.reduce((acumulador, valorTurma) => acumulador + valorTurma.qtdProf, 0),
      qtdVisit: element.progressao.reduce((acumulador, valorTurma) => acumulador + valorTurma.qtdVisit, 0),
      prof: element.progressao.reduce((acumulador, valorTurma) => acumulador + valorTurma.prof, 0),
    };
    element.progressao.push(novo);
  });

  const controle = Array(doc[0].progressao.length).fill(0);
  const crescimento = [];
  doc.forEach((element) => {
    element.progressao.forEach((fichaGrupo, i) => {
      controle[i] += fichaGrupo.qtdVisit;
    });
  });
  doc[0].progressao.forEach((e, i) => {
    e.qtdVisit = controle[i];
    e.qtd = doc[doc.length - 1].progressao[i].qtd;
    crescimento.push({ ...e, qtdAlunosFim: doc[doc.length - 1].progressao[i].qtdAlunos });
  });

  return { diasOrdenados: doc, crescimento };
}

/**
 * Busca os relatórios de um mês/ano ESPECÍFICO (não só o mês atual).
 *
 * CORREÇÃO DE BUG: RelatorioMensal.js antes filtrava só por `data.mes`,
 * sem `data.ano` — numa escola com mais de um ano de uso, os relatórios
 * de julho/2025 e julho/2026 apareceriam misturados na mesma tela. O
 * Firestore não faz filtro composto sem índice aqui (where duplo em
 * campos diferentes exigiria índice composto), então filtramos por mês
 * no Firestore e por ano no client — mesmo padrão já usado em
 * `gerarRelatoriosDoMes` abaixo.
 *
 * @param {string} idEscola
 * @param {number} mes - 1 a 12
 * @param {number} ano
 * @return {Promise<Array>} docs de `relatorio` daquele mês/ano específico.
 */
export async function buscarRelatoriosDoMes(idEscola, mes, ano) {
  const snap = await getDocs(
    query(collection(db, "escolas", idEscola, "relatorio"), where("data.mes", "==", mes)),
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => r.data?.ano === ano);
}

/**
 * Lista todos os meses/anos que já têm pelo menos 1 relatório gerado,
 * mais recente primeiro — alimenta a tela de histórico
 * (HistoricoRelatorios.js).
 *
 * Lê a coleção `relatorio` inteira e agrupa no client (Firestore não tem
 * "distinct"/GROUP BY). Aceitável na escala desta aplicação (no máximo
 * alguns dias de lição por semana, por escola); se algum dia isso virar
 * um gargalo, o caminho é manter um doc-resumo à parte (tipo
 * `dashboard/mesesComRelatorio`) atualizado por Cloud Function a cada
 * novo `relatorio` criado, em vez de reler a coleção toda aqui.
 *
 * @param {string} idEscola
 * @return {Promise<Array<{ano:number, mes:number, quantidade:number}>>}
 */
export async function listarMesesComRelatorio(idEscola) {
  const snap = await getDocs(collection(db, "escolas", idEscola, "relatorio"));

  const porChave = new Map();
  snap.docs.forEach((d) => {
    const r = d.data();
    if (!r.data?.ano || !r.data?.mes) return;
    const chave = `${r.data.ano}-${r.data.mes}`;
    const atual = porChave.get(chave) || { ano: r.data.ano, mes: r.data.mes, quantidade: 0 };
    atual.quantidade += 1;
    porChave.set(chave, atual);
  });

  return Array.from(porChave.values()).sort((a, b) => (b.ano - a.ano) || (b.mes - a.mes));
}

/**
 * Gera (retroativamente) os relatórios que estiverem faltando para um
 * mês/ano já passado. Para cada "aula" (dia de lição) daquele mês que:
 *   - ainda não tem um doc em `relatorio` correspondente, e
 *   - já teve a chamada fechada em TODAS as suas `aulaTurma`
 * ... monta e salva o `relatorio` daquele dia, e marca a aula como
 * "fechada" — exatamente o que a tela /relatorio faria manualmente.
 *
 * Dias com alguma turma sem chamada fechada são pulados (não dá pra
 * agregar dado que não existe) e reportados em `puladas`, para o diretor
 * saber que ainda precisa fazer a chamada daquele dia antes de gerar o
 * relatório dele.
 *
 * @param {string} idEscola
 * @param {number} mes - 1 a 12
 * @param {number} ano - ex.: 2026
 * @return {Promise<{geradas: Array, puladas: Array, erros: Array}>}
 */
export async function gerarRelatoriosDoMes(idEscola, mes, ano) {
  const resultado = { geradas: [], puladas: [], erros: [] };

  const aulasSnap = await getDocs(
    query(collection(db, "escolas", idEscola, "aulas"), where("data.mes", "==", mes)),
  );
  const aulasDoMes = aulasSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((a) => a.data?.ano === ano);

  if (aulasDoMes.length === 0) {
    resultado.avisoGeral = `Nenhuma aula cadastrada em ${mes}/${ano}.`;
    return resultado;
  }

  const relatoriosSnap = await getDocs(
    query(collection(db, "escolas", idEscola, "relatorio"), where("data.mes", "==", mes)),
  );
  const diasComRelatorio = new Set(
    relatoriosSnap.docs
      .map((d) => d.data())
      .filter((r) => r.data?.ano === ano)
      .map((r) => r.data.dia),
  );

  const turmasSnap = await getDocs(collection(db, "escolas", idEscola, "turmas"));
  const turmasDaEscola = turmasSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Busca a escola 1x só (fora do loop) — regionalId/geralId não mudam
  // entre os dias sendo processados no mesmo lote.
  const escolaSnap = await getDoc(doc(db, "escolas", idEscola));
  const escola = escolaSnap.exists() ? escolaSnap.data() : {};
  const dadosHierarquia = {
    idEscolaOrigem: idEscola,
    nomeEscolaOrigem: escola.nome || null,
    regionalId: escola.regionalId || null,
    geralId: escola.geralId || null,
  };

  for (const aula of aulasDoMes) {
    if (diasComRelatorio.has(aula.data?.dia)) {
      resultado.puladas.push({ aulaId: aula.id, dia: aula.data?.dia, motivo: "já tem relatório gerado" });
      continue;
    }
    if (!aula.turmas || aula.turmas.length === 0) {
      resultado.puladas.push({ aulaId: aula.id, dia: aula.data?.dia, motivo: "aula sem turmas associadas" });
      continue;
    }

    try {
      const aulaTurmasDoDia = await buscarPorIdsEmLotes(idEscola, "aulaTurma", aula.turmas);

      const naoFechadas = aulaTurmasDoDia.filter((t) => t.situacao !== "fechada");
      if (naoFechadas.length > 0) {
        resultado.puladas.push({
          aulaId: aula.id,
          dia: aula.data?.dia,
          motivo: `chamada pendente em: ${naoFechadas.map((t) => t.nome).join(", ")}`,
        });
        continue;
      }

      const relatorio = montarRelatorioDeAula(aulaTurmasDoDia, turmasDaEscola, aula);

      await addDoc(collection(db, "escolas", idEscola, "relatorio"), {
        ...relatorio,
        ...dadosHierarquia,
        createdAt: serverTimestamp(),
        geradoRetroativamente: true,
      });

      await updateDoc(doc(db, "escolas", idEscola, "aulas", aula.id), {
        situacao: "fechada",
        updatedAt: serverTimestamp(),
      });

      resultado.geradas.push({ aulaId: aula.id, dia: aula.data?.dia });
    } catch (error) {
      resultado.erros.push({ aulaId: aula.id, dia: aula.data?.dia, erro: error.message });
    }
  }

  return resultado;
}